import { VuexModule, Module, Mutation, Action } from "vuex-module-decorators";
import User from "@/models/user";
import IAuthenticationModel from "@/interfaces/authentication-model";
import {
  Api,
  setAuthorizationHeader,
  removeAuthorizationHeader,
} from "@/config";
import {
  AUTH_REQUEST,
  AUTH_ERROR,
  AUTH_SUCCESS,
  REMOVE_TOKEN,
  SET_TOKEN,
  LOGOUT,
  REMOVE_USER,
  SET_USER,
  UPDATE_USER,
  UPDATE_EMAIL,
} from "@/store/mutation-types";
import Jwt from "@/models/jwt";
import IProfile from "@/interfaces/profile";
import IChangeEmailModel from "@/interfaces/change-email-model";
import IConfirmationCode from "@/interfaces/confirmation-code";
import ISignupUpdateModel from "@/interfaces/signup-update-model";
import IChangeEmailRequestModel from "@/interfaces/change-email-request-model";
import IChangePassword from "@/interfaces/change-password";
import IResetPasswordModel from "@/interfaces/reset-password-model";

@Module({ namespaced: true })
export default class Authentication extends VuexModule {
  public status = "";
  public token: Jwt | null =
    (JSON.parse(localStorage.getItem("ucJwt") as string) as Jwt) || null;
  public user: User | null = null;
  get isAuthenticated(): boolean {
    return !!this.token;
  }

  @Action
  public checkEmailExists(email: string) {
    return new Promise((resolve, reject) => {
      if (email) {
        Api.post(`/accounts/register/check`, { email }).then(
          (response) => {
            if (response && response.status === 200) {
              resolve();
            }
          },
          (error) => {
            reject(error.response);
          }
        );
      } else {
        const error = "Registration failed";
        reject(error);
      }
    });
  }

  @Action
  public deleteUser(confirmation: string) {
    return new Promise((resolve, reject) => {
      if (confirmation === "DELETE") {
        Api.post(`/accounts/delete`, {
          Confirmation: confirmation,
        }).then(
          (response) => {
            if (response && response.status === 200) {
              resolve();
            }
          },
          (error) => {
            reject(error.response);
          }
        );
      } else {
        const error = "Account deletion failed";
        reject(error);
      }
    });
  }

  @Action
  public changeEmail(postModel: IChangeEmailRequestModel): Promise<void> {
    return new Promise((resolve, reject) => {
      Api.post(`/accounts/changeemail`, postModel).then(
        (response) => {
          if (response && response.status === 200) {
            resolve();
          }
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  @Action
  public changePassword(postModel: IChangePassword): Promise<void> {
    return new Promise((resolve, reject) => {
      Api.post(`/accounts/changepassword`, postModel).then(
        (response) => {
          if (response && response.status === 200) {
            resolve();
          }
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  @Action
  public emailConfirmation(postModel: IConfirmationCode) {
    return new Promise((resolve, reject) => {
      if (postModel) {
        Api.post(`/accounts/confirmEmail`, postModel).then(
          (response) => {
            if (response && response.status === 200) {
              resolve();
            }
          },
          (error) => {
            reject(error.response);
          }
        );
      } else {
        const error = "Could not confirm email";
        reject(error);
      }
    });
  }

  @Action
  public confirmChangeEmail(postModel: IChangeEmailModel) {
    return new Promise((resolve, reject) => {
      if (postModel) {
        Api.post(`/accounts/confirmChangeEmail`, postModel).then(
          (response) => {
            if (response && response.status === 200) {
              this.context.commit(UPDATE_EMAIL, response.data);
              resolve();
            }
          },
          (error) => {
            reject(error.response);
          }
        );
      } else {
        const error = "Could not verify the changed email";
        reject(error);
      }
    });
  }

  @Action
  public getUser() {
    return new Promise((resolve, reject) => {
      Api.get("/accounts").then((response) => {
        if (response && response.status === 200) {
          this.context.commit(SET_USER, response.data);
          resolve();
        } else {
          reject();
        }
      });
    });
  }

  @Action
  public resetPassword(postModel: IResetPasswordModel): Promise<void> {
    return new Promise((resolve, reject) => {
      Api.post(`/accounts/resetpassword`, postModel).then(
        (response) => {
          if (response && response.status === 200) {
            resolve();
          }
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  @Action
  public sendPasswordResetEmail(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      Api.post(`/accounts/sendpasswordresetemail`, {
        email,
      }).then(
        (response) => {
          if (response && response.status === 200) {
            resolve();
          }
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  @Action
  public signup(email: string) {
    return new Promise((resolve, reject) => {
      if (email) {
        Api.post(`/accounts/register`, { email }).then(
          (response) => {
            if (response && response.status === 200) {
              resolve();
            }
          },
          (error) => {
            reject(error.response);
          }
        );
      } else {
        const error = "Registration failed";
        reject(error);
      }
    });
  }

  @Action
  public sendConfirmationEmail(emailAddress: string): Promise<void> {
    return new Promise((resolve, reject) => {
      Api.post(`/accounts/sendconfirmationemail`, {
        email: emailAddress,
      }).then(
        (response) => {
          if (response && response.status === 200) {
            resolve();
          }
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  @Action
  public sendResetCode(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      Api.post(`/accounts/reset`, { email: email }).then(
        (response) => {
          if (response && response.status === 200) {
            resolve();
          }
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  @Action
  public signupUpdate(postModel: ISignupUpdateModel): Promise<void> {
    return new Promise((resolve, reject) => {
      if (postModel) {
        Api.post(`/accounts/register/update`, postModel).then(
          (response) => {
            if (response && response.status === 200) {
              resolve();
            }
          },
          (error) => {
            reject(error.response);
          }
        );
      } else {
        const error = "Registration failed";
        reject(error);
      }
    });
  }

  @Action
  public updateUser(postModel: IProfile) {
    return new Promise((resolve, reject) => {
      Api.put(`/accounts/update`, postModel).then(
        (response) => {
          if (response && response.status === 200) {
            let user = new User(response.data);
            this.context.commit(UPDATE_USER, user);
            resolve();
          }
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  @Action
  public login(authModel: IAuthenticationModel) {
    let apiCall = `/auth/login`;
    //
    // Checking the postModel to see
    // if login or refreshtoken should be called
    if (authModel.refreshToken) {
      apiCall = `/auth/refreshtoken`;
    }
    return new Promise((resolve, reject) => {
      this.context.commit(AUTH_REQUEST);

      Api.post(apiCall, authModel).then(
        (response) => {
          if (response && response.status === 200) {
            //
            // Commit success status
            this.context.commit(AUTH_SUCCESS, response.data.user);

            //
            // Set the token:
            // - Store
            // - localStorage
            const jwt = new Jwt(
              response.data.accessToken,
              response.data.refreshToken
            );
            this.context.commit(SET_TOKEN, jwt);

            resolve();
          } else {
            this.context.commit(AUTH_ERROR);
            this.context.commit(REMOVE_TOKEN);

            const error = "Login failed";
            reject(error);
          }
        },
        () => {
          this.context.commit(AUTH_ERROR);
          this.context.commit(REMOVE_TOKEN);

          const error = "Login failed";
          reject(error);
        }
      );
    });
  }

  @Action
  public logout() {
    return new Promise((resolve) => {
      this.context.commit(LOGOUT);
      this.context.commit(REMOVE_USER);
      this.context.commit(REMOVE_TOKEN);

      removeAuthorizationHeader();

      resolve();
    });
  }

  @Mutation
  public [AUTH_ERROR]() {
    this.status = "error";
  }

  @Mutation
  public [AUTH_REQUEST]() {
    this.status = "loading";
  }

  @Mutation
  public [AUTH_SUCCESS](user: User) {
    this.status = "success";
    this.user = user;
  }

  @Mutation
  public [LOGOUT]() {
    this.status = "";
  }

  @Mutation
  public [SET_TOKEN](jwt: Jwt) {
    if (jwt) {
      localStorage.setItem("ucJwt", JSON.stringify(jwt));
      this.token = jwt;

      setAuthorizationHeader(jwt.accessToken.token);
    }
  }

  @Mutation
  public [SET_USER](user: User) {
    this.user = user;
  }

  @Mutation
  public [REMOVE_TOKEN]() {
    this.token = null;
    localStorage.removeItem("ucJwt");
  }

  @Mutation
  public [REMOVE_USER]() {
    this.user = null;
  }

  // ****
  // **** USER RELATED
  // ****
  @Mutation
  public [UPDATE_EMAIL](newEmail: string) {
    if (this.user) {
      this.user.email = newEmail;
    }
  }

  @Mutation
  public [UPDATE_USER](updatedUser: User) {
    if (this.user && this.user.id === updatedUser.id) {
      this.user.firstName = updatedUser.firstName;
      this.user.lastName = updatedUser.lastName;
      this.user.companyName = updatedUser.companyName;
    }
  }
}
